import React from "react";
import { motion } from "framer-motion";
import { Section, Reveal, HairRule, Mono } from "./NetworkAtoms";
import GrowthCounter from "./GrowthCounter";

/**
 * Cited public figures only — deliberately no Connexis volumes.
 * Sources are printed on the page because lenders will check them.
 */
const STATS = [
  {
    big: "636,892", unit: "",
    label: "New UK companies incorporated in the first nine months of 2025 — every one of them a future borrower",
    source: "Companies House · quarterly incorporations, 2025",
  },
  {
    big: "£62.1bn", unit: "",
    label: "Gross bank lending to smaller UK businesses in 2024",
    source: "British Business Bank · Small Business Finance Markets 2025",
  },
  {
    big: "60", unit: "%",
    label: "Of that lending came from challenger and specialist banks — a record share",
    source: "British Business Bank · 2025",
  },
  {
    big: "£33bn", unit: "",
    label: "SME lending originated by NACFB member brokers in 2025, up 25% year on year",
    source: "NACFB · 2025",
  },
  {
    big: "~70", unit: "%",
    label: "Of commercial lenders' SME lending reaches them through broker channels",
    source: "NACFB membership survey",
  },
  {
    big: "43", unit: "%",
    label: "Share of smaller businesses using external finance",
    source: "British Business Bank · Small Business Finance Markets 2025",
  },
];

const QUARTERS = [
  { label: "Q1 2025", value: 211112 },
  { label: "Q2 2025", value: 209798 },
  { label: "Q3 2025", value: 215982 },
];

const NOTES = [
  {
    head: "More businesses, same problem",
    body: "Company formation is at record levels. Most founders have no idea where to start when they need capital — which product fits, or which lender funds their sector. That gap is the introducer's job.",
  },
  {
    head: "What that means in practice",
    body: "A business owner rarely finds the right lender alone, and a specialist lender can't afford a national sales force. The introducer is the bridge — usually held together with email and spreadsheets.",
  },
  {
    head: "Where Connexis sits",
    body: "Between the two, as infrastructure: one case shape, criteria-driven routing, consent captured properly, automated chasing, and commission stated without an invoice. Everyone gets paid on time.",
  },
];

export default function MarketContext() {
  return (
    <div className="relative">
      <div aria-hidden className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(0,85,255,0.16),transparent_65%)]" />
      <Section eyebrow="The market behind it" testId="network-market"
               title="UK business lending is intermediated and specialist-led."
               lede="We're not claiming a gap nobody's noticed. The market already runs through introducers and specialist lenders — what it lacks is infrastructure between the two that behaves properly.">
        <div className="mt-14 divide-y divide-white/[0.07] border-y border-white/[0.07]">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.06}>
              <div className="grid sm:grid-cols-12 gap-4 sm:gap-8 items-baseline py-8 sm:py-10 group"
                   data-testid={`market-stat-${i}`}>
                <div className="sm:col-span-4">
                  <span className="font-cabinet text-[46px] sm:text-[64px] leading-[0.9] tracking-[-0.04em] font-semibold text-white">
                    {s.big}
                  </span>
                  <span className="font-cabinet text-[30px] sm:text-[40px] font-semibold text-[#7da9ff]">{s.unit}</span>
                </div>
                <div className="sm:col-span-8">
                  <p className="text-[15.5px] sm:text-[17px] leading-snug text-slate-200">{s.label}</p>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">{s.source}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 sm:p-8" data-testid="market-quarters">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <Mono>Quarterly incorporations · 2025</Mono>
              <span className="text-[11.5px] text-slate-500">Source: Companies House</span>
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-7">
              {QUARTERS.map((q, i) => {
                const peak = Math.max(...QUARTERS.map((x) => x.value));
                return (
                  <div key={q.label} data-testid={`market-quarter-${i}`}>
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">{q.label}</p>
                    <p className="mt-1.5 font-cabinet text-[30px] sm:text-[34px] leading-none tracking-[-0.03em] font-semibold text-white">
                      {q.value.toLocaleString("en-GB")}
                    </p>
                    <div className="mt-3.5 h-1.5 rounded-full bg-white/[0.07] overflow-hidden">
                      <motion.div className="h-full rounded-full bg-[#0055FF]"
                                  initial={{ width: 0 }}
                                  whileInView={{ width: `${(q.value / peak) * 100}%` }}
                                  viewport={{ once: true, margin: "-60px" }}
                                  transition={{ duration: 1.1, delay: 0.1 + i * 0.12, ease: [0.16, 1, 0.3, 1] }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-7 text-[14.5px] sm:text-[15.5px] leading-snug text-slate-200">
              <span className="text-white font-semibold">636,892 new companies in nine months</span> — and that's
              before the millions of limited companies already trading in the UK. More businesses means more demand
              for capital, and more demand for someone who knows how to place it.
            </p>
          </div>
        </Reveal>

        <GrowthCounter />

        <Reveal delay={0.1}>
          <div className="mt-12 grid lg:grid-cols-3 gap-x-8 gap-y-9">
            {NOTES.map((n) => (
              <div key={n.head} className="border-t border-white/[0.09] pt-5">
                <Mono className="block leading-none">{n.head}</Mono>
                <p className="mt-4 text-[14.5px] leading-[1.62] text-slate-400">{n.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 rounded-2xl border border-[#0055FF]/25 bg-[#0055FF]/[0.06] p-6 sm:p-8"
               data-testid="market-illustration">
            <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-start">
              <div className="lg:col-span-8">
                <Mono>A worked illustration</Mono>
                <p className="mt-4 text-[15.5px] sm:text-[17px] leading-snug text-slate-100">
                  Twenty-five funded introductions in a year, at a typical commercial finance commission, puts an
                  introducer in six-figure territory — without chasing volume, because each one is a business that
                  needed the funding anyway.
                </p>
              </div>
              <div className="lg:col-span-4 lg:border-l lg:border-white/[0.09] lg:pl-8">
                <p className="text-[11.5px] leading-relaxed text-slate-500">
                  Illustration only. It is not a forecast, a projection or an earnings claim. Actual commission
                  depends entirely on deal size, product, lender terms and whether a case completes.
                </p>
              </div>
            </div>
          </div>

          <HairRule className="mt-14" />
          <p className="mt-5 text-[11.5px] text-slate-600 max-w-3xl">
            Figures are third-party market data, quoted for context and correct at the date of the cited publication.
            They are not Connexis performance figures, forecasts, or an indication of any outcome. Nothing on this page
            is a credit agreement or an offer of finance.
          </p>
        </Reveal>
      </Section>
    </div>
  );
}
